/**
 * Scheduled Reports Page
 * Manage automated query reports with email notifications
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  getScheduledReports, createScheduledReport, deleteScheduledReport,
  toggleScheduledReport, runScheduledReportNow, getEmailConfig
} from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  Mail,
  Plus,
  Trash2,
  Play,
  Pause,
  FileText,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Settings
} from 'lucide-react';

const ScheduledReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [emailConfig, setEmailConfig] = useState({ configured: false });
  const [creating, setCreating] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    query: '',
    schedule: 'daily',
    scheduleTime: '09:00',
    recipients: '',
    format: 'csv'
  });

  const fetchReports = useCallback(async () => {
    try {
      const data = await getScheduledReports();
      setReports(data.reports || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmailConfig = useCallback(async () => {
    try {
      const config = await getEmailConfig();
      setEmailConfig(config);
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchEmailConfig();
  }, [fetchReports, fetchEmailConfig]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.query.trim() || !form.recipients.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const recipientList = form.recipients.split(',').map(e => e.trim()).filter(e => e);
      await createScheduledReport(
        form.name,
        form.query,
        form.schedule,
        form.scheduleTime,
        recipientList,
        form.format
      );
      toast.success('Scheduled report created!');
      setShowCreateDialog(false);
      setForm({ name: '', query: '', schedule: 'daily', scheduleTime: '09:00', recipients: '', format: 'csv' });
      fetchReports();
    } catch (error) {
      toast.error('Failed to create report');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await deleteScheduledReport(reportId);
      toast.success('Report deleted');
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleToggle = async (reportId) => {
    try {
      const result = await toggleScheduledReport(reportId);
      toast.success(result.enabled ? 'Report enabled' : 'Report disabled');
      fetchReports();
    } catch (error) {
      toast.error('Failed to toggle report');
    }
  };

  const handleRunNow = async (reportId) => {
    try {
      await runScheduledReportNow(reportId);
      toast.success('Report execution started!');
      setTimeout(fetchReports, 2000);
    } catch (error) {
      toast.error('Failed to run report');
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <Badge variant="outline" className="text-gray-500">Never run</Badge>;
    if (status === 'running') return <Badge className="bg-blue-500">Running</Badge>;
    if (status === 'completed') return <Badge className="bg-green-500">Completed</Badge>;
    if (status.startsWith('failed')) return <Badge className="bg-red-500">Failed</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  const getScheduleLabel = (schedule) => {
    switch (schedule) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      default: return schedule;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="scheduled-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Scheduled Reports</h1>
          <p className="text-gray-500 text-sm mt-1">Automate query reports and receive them via email</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="btn-gold" data-testid="create-report-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Report
        </Button>
      </div>

      {/* Email Config Status */}
      {!emailConfig.configured && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="text-amber-800 font-medium">Email notifications not configured</p>
              <p className="text-amber-600 text-sm">Reports will run but emails won't be sent. Configure AWS SES in environment variables.</p>
            </div>
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              <Settings className="w-3 h-3 mr-1" />
              Config needed
            </Badge>
          </div>
        </Card>
      )}

      {/* Reports List */}
      <div className="grid gap-4">
        {reports.length === 0 ? (
          <Card className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No scheduled reports yet</p>
            <p className="text-gray-400 text-sm mt-1">Create your first automated report</p>
            <Button 
              onClick={() => setShowCreateDialog(true)} 
              variant="outline" 
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Report
            </Button>
          </Card>
        ) : (
          reports.map(report => (
            <Card key={report.id} className="p-4" data-testid={`report-${report.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-800">{report.name}</h3>
                    {getStatusBadge(report.last_status)}
                    {!report.enabled && (
                      <Badge variant="outline" className="text-gray-400">Disabled</Badge>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{report.query}</p>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {getScheduleLabel(report.schedule)} at {report.schedule_time}
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />
                      {report.recipients?.length || 0} recipient{(report.recipients?.length || 0) !== 1 ? 's' : ''}
                    </div>
                    <div className="flex items-center gap-1">
                      <Play className="w-3.5 h-3.5" />
                      {report.run_count || 0} runs
                    </div>
                    {report.next_run && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        Next: {new Date(report.next_run).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={report.enabled}
                    onCheckedChange={() => handleToggle(report.id)}
                    className="data-[state=checked]:bg-[#D4AF37]"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRunNow(report.id)}
                    disabled={!report.enabled}
                    title="Run now"
                  >
                    <Play className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(report.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              Create Scheduled Report
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Set up an automated query to run on a schedule
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Report Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Daily Active Users Report"
                className="border-gray-200"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Query *</label>
              <Textarea
                value={form.query}
                onChange={(e) => setForm(prev => ({ ...prev, query: e.target.value }))}
                placeholder="Enter your natural language query..."
                rows={3}
                className="border-gray-200"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Schedule</label>
                <Select
                  value={form.schedule}
                  onValueChange={(value) => setForm(prev => ({ ...prev, schedule: value }))}
                >
                  <SelectTrigger className="border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly (Mondays)</SelectItem>
                    <SelectItem value="monthly">Monthly (1st)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Time (UTC)</label>
                <Input
                  type="time"
                  value={form.scheduleTime}
                  onChange={(e) => setForm(prev => ({ ...prev, scheduleTime: e.target.value }))}
                  className="border-gray-200"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Recipients *</label>
              <Input
                value={form.recipients}
                onChange={(e) => setForm(prev => ({ ...prev, recipients: e.target.value }))}
                placeholder="email1@example.com, email2@example.com"
                className="border-gray-200"
              />
              <p className="text-xs text-gray-400">Separate multiple emails with commas</p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Export Format</label>
              <Select
                value={form.format}
                onValueChange={(value) => setForm(prev => ({ ...prev, format: value }))}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="btn-gold">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledReportsPage;
